// İş Ortağı — şifre değiştirme penceresi (kullanıcı isteği 16.10.2026: "pop up gibi").
// İki kullanım: (1) ilk giriş (`must_change_password`) — ZORUNLU, kapatılamaz; yalnız
// "Çıkış yap" seçeneği vardır. (2) Üst çubuk › kullanıcı menüsü › Şifreyi değiştir — kapatılabilir.
// Sunucu ucu iç güvenlik sayfasıyla aynı (`PUT /auth/change-password`); değişince
// oturumdaki bayrak düşürülür, pencere bir daha açılmaz.
import { useEffect, useRef, useState } from 'react'
import { KeyRound, Eye, EyeOff, ShieldCheck, X, LogOut } from 'lucide-react'
import { authService } from '../../../services/authService'
import { useAuthStore } from '../../../stores/authStore'
import { toast } from '../../../stores/uiStore'
import { apiErrorMessage } from '../../../utils/apiError'
import { PSpin } from './ui'

const MIN_LEN = 8

export default function PartnerPasswordModal({ forced = false, onClose, onLogout }) {
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)
  const [form, setForm] = useState({ old: '', next: '', confirm: '' })
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const firstRef = useRef(null)
  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setErr('') }

  useEffect(() => { firstRef.current?.focus() }, [])
  useEffect(() => {
    if (forced) return undefined
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [forced, busy, onClose])

  const tooShort = form.next.length > 0 && form.next.length < MIN_LEN
  const mismatch = form.confirm.length > 0 && form.next !== form.confirm
  const canSubmit = form.old && form.next.length >= MIN_LEN && form.next === form.confirm && !busy

  const submit = async (e) => {
    e.preventDefault()
    if (form.next === form.old) { setErr('Yeni şifre eskisiyle aynı olamaz.'); return }
    if (!canSubmit) return
    setBusy(true)
    try {
      await authService.changePassword(form.old, form.next, form.confirm)
      if (user) setUser({ ...user, must_change_password: false })
      toast.success('Şifreniz değiştirildi')
      onClose?.()
    } catch (e2) {
      setErr(apiErrorMessage(e2) || 'Şifre değiştirilemedi.')
    } finally {
      setBusy(false)
    }
  }

  const type = show ? 'text' : 'password'
  const eye = (
    <button type="button" className="icon-btn" style={{ background: 'transparent', width: 32, height: 32 }}
            aria-label={show ? 'Şifreleri gizle' : 'Şifreleri göster'} aria-pressed={show} onClick={() => setShow((v) => !v)}>
      {show ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  )

  return (
    <div className="modal-backdrop" role="presentation"
         onMouseDown={() => { if (!forced && !busy) onClose?.() }}>
      <form className="modal" role="dialog" aria-modal="true" aria-labelledby="pp-pw-title"
            style={{ width: 440 }} onMouseDown={(e) => e.stopPropagation()} onSubmit={submit} noValidate>
        <div className="modal__head">
          <span className="icon-box icon-box--sm"><KeyRound size={16} /></span>
          <h2 id="pp-pw-title" className="modal__title" style={{ flexGrow: 1 }}>
            {forced ? 'Şifrenizi belirleyin' : 'Şifreyi değiştir'}
          </h2>
          {!forced && (
            <button type="button" className="icon-btn" style={{ background: 'transparent', width: 30, height: 30 }}
                    aria-label="Kapat" onClick={() => onClose?.()} disabled={busy}>
              <X size={16} />
            </button>
          )}
        </div>

        <div className="modal__body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {forced && (
            <div className="callout" style={{ alignItems: 'flex-start' }}>
              <ShieldCheck size={18} style={{ marginTop: 1 }} />
              <span>İlk girişiniz. Size iletilen geçici şifre yerine yalnız sizin bildiğiniz bir şifre belirleyin.</span>
            </div>
          )}
          {err && <div className="callout callout--error" role="alert">{err}</div>}

          <div className="field">
            <label className="field__label" htmlFor="pp-pw-old">{forced ? 'Geçici şifre' : 'Mevcut şifre'}</label>
            <div className="input-group">
              <input id="pp-pw-old" ref={firstRef} type={type} autoComplete="current-password"
                     value={form.old} onChange={set('old')} />
              {eye}
            </div>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="pp-pw-new">Yeni şifre</label>
            <div className="input-group" aria-invalid={tooShort ? 'true' : undefined}>
              <input id="pp-pw-new" type={type} autoComplete="new-password" value={form.next} onChange={set('next')} />
            </div>
            <small className={tooShort ? '' : 'subtle'} style={tooShort ? { color: 'var(--c-danger-fg)' } : undefined}>
              En az {MIN_LEN} karakter.
            </small>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="pp-pw-confirm">Yeni şifre (tekrar)</label>
            <div className="input-group" aria-invalid={mismatch ? 'true' : undefined}>
              <input id="pp-pw-confirm" type={type} autoComplete="new-password" value={form.confirm} onChange={set('confirm')} />
            </div>
            {mismatch && <small style={{ color: 'var(--c-danger-fg)' }}>Şifreler eşleşmiyor.</small>}
          </div>
        </div>

        <div className="modal__foot">
          {forced ? (
            <button type="button" className="btn btn--ghost" onClick={onLogout} disabled={busy} style={{ marginRight: 'auto' }}>
              <LogOut size={16} />Çıkış yap
            </button>
          ) : (
            <button type="button" className="btn btn--secondary" onClick={() => onClose?.()} disabled={busy}>Vazgeç</button>
          )}
          <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
            {busy ? <PSpin /> : <KeyRound size={16} />}Şifreyi kaydet
          </button>
        </div>
      </form>
    </div>
  )
}
